import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import { useEntitlements } from '@/hooks/useEntitlements';
import { activeItem, surfaceForPath, visibleSurfaces } from '@/surfaces';

/**
 * The active surface's items (JSS#1154). Renders nothing outside a surface
 * (e.g. /event/:slug) and for single-item surfaces, where the header tab is the page.
 */
export default function Sidebar() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const { entitlements } = useEntitlements();
  // A disabled surface (#1160) is not visible, so its pages get no sidebar.
  const surface = surfaceForPath(visibleSurfaces(user, entitlements?.surfaces), pathname);
  if (!surface || surface.items.length < 2) return null;
  const current = activeItem(surface, pathname);

  return (
    <aside
      aria-label={`${surface.label} navigation`}
      className="w-52 shrink-0 border-r border-jeket-gold/20 bg-jeket-black-surface overflow-y-auto"
    >
      <p className="px-4 pt-4 pb-2 text-[10px] font-medium text-jeket-silver-muted uppercase tracking-wider">
        {surface.label}
      </p>
      <nav className="flex flex-col gap-0.5 px-2 pb-4">
        {surface.items.map(({ path, label, icon: Icon }) => {
          const active = current?.path === path;
          return (
            <Link
              key={path}
              to={path}
              aria-current={active ? 'page' : undefined}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors',
                active
                  ? 'text-jeket-gold bg-jeket-gold/10'
                  : 'text-jeket-silver-muted hover:text-jeket-silver-bright hover:bg-jeket-black-elevated',
              )}
            >
              <Icon size={14} />
              {label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}
